import React from 'react'
import classNames from 'classnames'
import { motion } from 'framer-motion'

import { NEUTRAL } from 'components/constants'
import {
  SMALL,
  MEDIUM,
  LARGE,
  XLARGE,
  XXLARGE,
  TINY,
} from 'components/constants'
import {
  BANK,
  CARD,
  CASH,
  CHEQUE,
  CHEVRON,
  CLOSE,
  CUSTOMER,
  DATE,
  DECREMENT,
  DOLLAR,
  EXPAND,
  GRID,
  INCREMENT,
  LIST,
  LOCK,
  MENU,
  MINIMISE,
  NUMBER,
  REGISTER,
  SEARCH,
  VOUCHER,
  UPDATE,
  PERCENTAGE
} from 'components/constants'

const Icon = ({
  animate,
  className,
  delay,
  rotate,
  shade = 2,
  size = SMALL,
  style,
  theme = NEUTRAL,
  type,
}) => {
  const strokeColor = getStrokeColor(theme)
  const fillColor = getFillColor(theme)

  function getSize() {
    switch (size) {
      case TINY:
        return 16
      case SMALL:
        return 24
      case MEDIUM:
        return 32
      case LARGE:
        return 38
      case XLARGE:
        return 54
      case XXLARGE:
        return 85
      default:
        return size
    }
  }

  function getStrokeColor(theme) {
    if (theme)
      switch (shade) {
        case 0:
          return 'var(--sc-shade-7)'
        case 4:
        case 5:
        case 6:
          return `var(--sc-${theme.toLowerCase()}-2)`
        case 7:
          return 'white'
        default:
          return `var(--sc-${theme.toLowerCase()}-${shade + 1})`
      }
  }

  function getFillColor(theme) {
    if (!theme) return
    return `var(--sc-${theme.toLowerCase()}-${shade})`
  }

  function transition() {
    const origin = (getSize() / 4) * 0.01

    if (animate) {
      return {
        style: {
          originX: origin,
          originY: origin,
        },
        initial: {
          opacity: 0,
          scale: 0.8,
        },
        animate: {
          opacity: 1,
          scale: 1,
        },
        exit: {
          opacity: 0,
          scale: 0,
        },
        transition: {
          delay: delay ? 0.3 : 0,
        },
      }
    }
  }

  return (
    <motion.svg
      width={getSize()}
      className={classNames({
        'no-shrink': true,
        ['rotate-' + rotate]: rotate,
        [className]: className,
      })}
      style={style}
      viewBox='0 0 100 100'
      {...transition(size)}>
      {getGlyph()}
    </motion.svg>
  )

  function getGlyph() {
    const rounded = 6
    const thickness = 5

    switch (type) {
      case BANK:
        return (
          <g
            transform='translate(7.000000, 14.183746)'
            stroke={strokeColor}
            strokeWidth='5'>
            <rect
              fill={fillColor}
              x='5'
              y='22.8162537'
              width='75'
              height='42'
              rx='3'></rect>
            <rect
              fill={fillColor}
              x='0'
              y='60.8162537'
              width='85'
              height='10'
              rx='3'></rect>
            <line
              x1='24.5'
              y1='25.2899379'
              x2='24.5'
              y2='60.3425695'
              id='Line'
              strokeLinecap='square'></line>
            <line
              x1='60.5'
              y1='25.2899379'
              x2='60.5'
              y2='60.3425695'
              id='Line'
              strokeLinecap='square'></line>
            <path
              fill={fillColor}
              d='M2.01243783,14.1146821 L42.5,0 L42.5,0 L82.9875622,14.1146821 C84.1926219,14.5347873 85,15.6712874 85,16.947476 L85,19.8162537 C85,21.4731079 83.6568542,22.8162537 82,22.8162537 L3,22.8162537 C1.34314575,22.8162537 2.02906125e-16,21.4731079 0,19.8162537 L0,16.947476 C-1.85651279e-15,15.6712874 0.807378096,14.5347873 2.01243783,14.1146821 Z'></path>
          </g>
        )
      case CARD:
        return (
          <g transform='translate(4.000000, 25.000000)'>
            <rect
              stroke={strokeColor}
              strokeWidth={thickness}
              fill={fillColor}
              x='0.5'
              y='-2.5'
              width='90'
              height='56'
              rx='3'></rect>
            <path
              fill={strokeColor}
              d='M38.5,35 C39.8807119,35 41,36.1192881 41,37.5 C41,38.8807119 39.8807119,40 38.5,40 L16.5,40 C15.1192881,40 14,38.8807119 14,37.5 C14,36.1192881 15.1192881,35 16.5,35 L38.5,35 Z M51.5,35 C52.8807119,35 54,36.1192881 54,37.5 C54,38.8807119 52.8807119,40 51.5,40 L47.5,40 C46.1192881,40 45,38.8807119 45,37.5 C45,36.1192881 46.1192881,35 47.5,35 L51.5,35 Z'></path>
            <path
              fill={strokeColor}
              d='M88.5,20 C89.8807119,20 91,21.1192881 91,22.5 C91,23.8807119 89.8807119,25 88.5,25 L2.5,25 C1.11928813,25 1.69088438e-16,23.8807119 0,22.5 C-1.69088438e-16,21.1192881 1.11928813,20 2.5,20 L88.5,20 Z M88.5,8 C89.8807119,8 91,9.11928813 91,10.5 C91,11.8807119 89.8807119,13 88.5,13 L2.5,13 C1.11928813,13 1.69088438e-16,11.8807119 0,10.5 C-1.69088438e-16,9.11928813 1.11928813,8 2.5,8 L88.5,8 Z'></path>
          </g>
        )
      case CASH:
        return (
          <g transform='translate(8.000000, 23.000000)'>
            <rect
              stroke={strokeColor}
              strokeWidth='5'
              fill={fillColor}
              x='3'
              y='0'
              width='85'
              height='50'></rect>
            <rect
              stroke={strokeColor}
              strokeWidth='5'
              fill={fillColor}
              x='-2.5'
              y='8.5'
              width='82'
              height='48'></rect>
            <path
              stroke={strokeColor}
              strokeWidth='5'
              fill={fillColor}
              d='M76.5,40.5 C76.7033605,40.5 76.905705,40.5043525 77.106946,40.5129627 L77.106946,40.5129627 L79.5000963,40.6153557 L79.499982,56.499957 L62.6383347,56.500247 C61.9803351,51.7392306 63.7173426,47.3563184 66.8192194,44.3864958 C69.3336257,41.9791338 72.7440565,40.5 76.5,40.5 L76.5,40.5 Z M-2.49937961,40.6382188 C2.1483569,39.9961397 6.43557865,41.6364775 9.39968179,44.6006919 C12.3637717,47.5648931 14.0039573,51.8521603 13.3617084,56.4999038 L13.3617084,56.4999038 L-2.50011608,56.500018 Z M13.3844597,8.49903288 L13.4870001,10.8921856 C13.495635,11.0937128 13.5,11.296347 13.5,11.5 C13.5,15.2557856 12.0209905,18.6660874 9.61380782,21.1804635 C6.64415874,24.2823529 2.26145768,26.0194776 -2.4993888,25.3617845 L-2.4993888,25.3617845 L-2.50010639,8.50018064 L13.3844597,8.49903288 Z M79.500018,8.49988392 L79.4999038,24.3617084 C74.8521603,25.0039573 70.5648931,23.3637717 67.6006919,20.3996818 C64.6364775,17.4355787 62.9961397,13.1483569 63.6382188,8.50062039 L63.6382188,8.50062039 L79.500018,8.49988392 Z'></path>
            <circle fill={fillColor} cx='38.5' cy='32.5' r='11.5'></circle>
          </g>
        )
      case VOUCHER:
      case CHEQUE:
        return (
          <g>
            <g
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={thickness}
              transform='translate(7.000000, 29.000000)'>
              <rect x='-2.5' y='-2.5' width={90} height='47' rx='3'></rect>
            </g>
            <path
              fill={strokeColor}
              d='M79.5,56 C80.8807119,56 82,57.1192881 82,58.5 C82,59.8807119 80.8807119,61 79.5,61 L57.5,61 C56.1192881,61 55,59.8807119 55,58.5 C55,57.1192881 56.1192881,56 57.5,56 L79.5,56 Z M79.5,47 C80.8807119,47 82,48.1192881 82,49.5 C82,50.8807119 80.8807119,52 79.5,52 L19.5,52 C18.1192881,52 17,50.8807119 17,49.5 C17,48.1192881 18.1192881,47 19.5,47 L79.5,47 Z M41.5,38 C42.8807119,38 44,39.1192881 44,40.5 C44,41.8807119 42.8807119,43 41.5,43 L19.5,43 C18.1192881,43 17,41.8807119 17,40.5 C17,39.1192881 18.1192881,38 19.5,38 L41.5,38 Z'></path>
          </g>
        )
      case CHEVRON:
        return (
          <g>
            <path
              fill={fillColor}
              transform='translate(0 8)'
              d='M56.7,75.75 L95.4,37.05 C98.8,33.65 98.8,28.05 95.4,24.65 C92,21.25 86.4,21.25 83,24.65 L50.5,57.05 L18,24.55 C14.6,21.15 9,21.15 5.6,24.55 C3.9,26.25 3,28.55 3,30.75 C3,33.05 3.9,35.25 5.6,36.95 L44.3,75.75 C47.7,79.15 53.3,79.15 56.7,75.75 Z'></path>
          </g>
        )
      case CUSTOMER:
        return (
          <g>
            <mask id='customer-mask' fill={fillColor}>
              <rect x={5} y={5} width={90} height={90} rx={30}></rect>
            </mask>
            <rect x={5} y={5} width={90} height={90} rx={30} fill={fillColor} />
            <circle
              stroke={strokeColor}
              fill={fillColor}
              strokeWidth={thickness}
              mask='url(#customer-mask)'
              cx='50.5625'
              cy='100.6875'
              r={35}
            />
            <circle
              stroke={strokeColor}
              fill={fillColor}
              strokeWidth={thickness}
              mask='url(#customer-mask)'
              cx={50}
              cy={48.5}
              r={17}
            />
          </g>
        )
      case DATE:
        return (
          <g>
            <path fill={fillColor} d="M79,10 C87.2842712,10 94,16.7157288 94,25 L94,78 C94,86.2842712 87.2842712,93 79,93 L21,93 C12.7157288,93 6,86.2842712 6,78 L6,25 C6,16.7157288 12.7157288,10 21,10 L79,10 Z M79,19 L21,19 C17.6862915,19 15,21.6862915 15,25 L15,78 C15,81.3137085 17.6862915,84 21,84 L79,84 C82.3137085,84 85,81.3137085 85,78 L85,25 C85,21.6862915 82.3137085,19 79,19 Z"></path>
            <polygon fill={fillColor} points="91.5 33.5 91.5 42.5 7.5 42.5 7.5 33.5"></polygon>
            <path fill={fillColor} d="M29.5,4 C31.9852814,4 34,6.01471863 34,8.5 L34,20.5 C34,22.9852814 31.9852814,25 29.5,25 C27.0147186,25 25,22.9852814 25,20.5 L25,8.5 C25,6.01471863 27.0147186,4 29.5,4 Z"></path>
            <path fill={fillColor} d="M70.5,4 C72.9852814,4 75,6.01471863 75,8.5 L75,20.5 C75,22.9852814 72.9852814,25 70.5,25 C68.0147186,25 66,22.9852814 66,20.5 L66,8.5 C66,6.01471863 68.0147186,4 70.5,4 Z" id="Line-6"></path>
          </g>
        )
      case EXPAND:
        return (
          <g fill={fillColor}>
            <path d="M21,49 C24.3137085,49 27,51.6862915 27,55 L27,73 L45,73 C48.2383969,73 50.8775718,75.5655749 50.9958615,78.7750617 L51,79 C51,82.3137085 48.3137085,85 45,85 L21,85 C17.6862915,85 15,82.3137085 15,79 L15,55 C15,51.6862915 17.6862915,49 21,49 Z"></path>
            <path
              d="M55,15 C58.3137085,15 61,17.6862915 61,21 L61,39 L79,39 C82.2383969,39 84.8775718,41.5655749 84.9958615,44.7750617 L85,45 C85,48.3137085 82.3137085,51 79,51 L55,51 C51.6862915,51 49,48.3137085 49,45 L49,21 C49,17.6862915 51.6862915,15 55,15 Z"
              transform="translate(67, 33) rotate(180) translate(-67, -33) "></path>
          </g>
        )
      case MINIMISE:
        const padding = 8
        const minimiseLength = 36
        const minimiseWidth = 12
        const minimiseRadius = 6

        return (
          <g>
            <g fill={fillColor}>
              <rect
                x={padding + minimiseLength}
                y={padding}
                width={minimiseWidth}
                height={minimiseLength}
                rx={minimiseRadius}
              />
              <rect
                x={padding + minimiseLength}
                y={padding + minimiseLength - minimiseWidth}
                width={minimiseLength}
                height={minimiseWidth}
                rx={minimiseRadius}
              />
            </g>
            <g fill={fillColor}>
              <rect
                x={padding}
                y={100 - minimiseLength - padding}
                width={minimiseLength}
                height={minimiseWidth}
                rx={minimiseRadius}
              />
              <rect
                x={padding + minimiseLength - minimiseWidth}
                y={100 - minimiseLength - padding}
                width={minimiseWidth}
                height={minimiseLength}
                rx={minimiseRadius}
              />
            </g>
          </g>
        )
      case LOCK:
        return (
          <g>
            <rect fill={fillColor} x={0} y={0} width={100} height={100} rx={30} />
            <path
              d='M50.5,46 C52.9852814,46 55,48.0147186 55,50.5 C55,52.9852814 52.9852814,55 50.5,55 C48.0147186,55 46,52.9852814 46,50.5 C46,48.0147186 48.0147186,46 50.5,46 Z M34.5,46 C36.9852814,46 39,48.0147186 39,50.5 C39,52.9852814 36.9852814,55 34.5,55 C32.0147186,55 30,52.9852814 30,50.5 C30,48.0147186 32.0147186,46 34.5,46 Z M66.5,46 C68.9852814,46 71,48.0147186 71,50.5 C71,52.9852814 68.9852814,55 66.5,55 C64.0147186,55 62,52.9852814 62,50.5 C62,48.0147186 64.0147186,46 66.5,46 Z M50.5,60 C52.9852814,60 55,62.0147186 55,64.5 C55,66.9852814 52.9852814,69 50.5,69 C48.0147186,69 46,66.9852814 46,64.5 C46,62.0147186 48.0147186,60 50.5,60 Z M34.5,60 C36.9852814,60 39,62.0147186 39,64.5 C39,66.9852814 36.9852814,69 34.5,69 C32.0147186,69 30,66.9852814 30,64.5 C30,62.0147186 32.0147186,60 34.5,60 Z M66.5,60 C68.9852814,60 71,62.0147186 71,64.5 C71,66.9852814 68.9852814,69 66.5,69 C64.0147186,69 62,66.9852814 62,64.5 C62,62.0147186 64.0147186,60 66.5,60 Z M50.5,32 C52.9852814,32 55,34.0147186 55,36.5 C55,38.9852814 52.9852814,41 50.5,41 C48.0147186,41 46,38.9852814 46,36.5 C46,34.0147186 48.0147186,32 50.5,32 Z M34.5,32 C36.9852814,32 39,34.0147186 39,36.5 C39,38.9852814 36.9852814,41 34.5,41 C32.0147186,41 30,38.9852814 30,36.5 C30,34.0147186 32.0147186,32 34.5,32 Z M66.5,32 C68.9852814,32 71,34.0147186 71,36.5 C71,38.9852814 68.9852814,41 66.5,41 C64.0147186,41 62,38.9852814 62,36.5 C62,34.0147186 64.0147186,32 66.5,32 Z'
              fill={strokeColor}
            />
          </g>
        )
      case REGISTER:
        return (
          <g>
            <mask id='mask-2' fill={fillColor}>
              <rect x={5} y={5} rx={30} width={90} height={90} />
            </mask>
            <rect x={5} y={5} rx={30} width={90} height={90} fill={fillColor} />
            <path
              d='M78.9110231,47.5 C80.1037994,47.5 81.1929433,47.9654021 82.0009051,48.7285127 C82.8088668,49.4916233 83.3356464,50.5524423 83.4036941,51.7432759 L86.3751226,103.743276 C86.446015,104.983893 86.0073367,106.135795 85.2407811,106.995267 C84.45677,107.87431 83.3297792,108.447451 82.054199,108.49674 L18.1175484,108.5 C16.8749077,108.5 15.7499077,107.99632 14.9355678,107.181981 C14.1026881,106.349101 13.5947751,105.191254 13.6183338,103.914947 L16.5963059,51.7432759 C16.6643536,50.5524423 17.1911332,49.4916233 17.9990949,48.7285127 C18.8070567,47.9654021 19.8962006,47.5 21.0889769,47.5 Z'
              stroke={strokeColor}
              strokeWidth={thickness}
              fill={fillColor}
              mask='url(#mask-2)'
            />
            <rect
              id='Rectangle'
              fill={fillColor}
              mask='url(#mask-2)'
              x='31'
              y='31'
              width='22'
              height='28'
            />
            <path
              d='M25,57.5 L58,57.5 M52.5,57.5 L52.5,30.5 M30.5,57.5 L30.5,30.5 M52.3611111,30.5 L30.6388889,30.5'
              stroke={strokeColor}
              strokeWidth={thickness}
              strokeLinecap='round'
              strokeLinejoin='round'
              mask='url(#mask-2)'
            />
            <path
              d='M71.5,69.5 L45.5,69.5 M59.5,78 L45.5,78 M71.5,78 L67.5,78'
              stroke={strokeColor}
              strokeWidth={thickness}
              strokeLinecap='round'
              strokeLinejoin='round'
              mask='url(#mask-2)'
            />
          </g>
        )
      case CLOSE:
        return (
          <path
            d='M26.513471,17.077445 L49.8008326,40.3648739 L73.0881943,17.077445 C75.7676223,14.3980169 80.1118335,14.3980169 82.7912616,17.077445 C85.4706897,19.756873 85.4706897,24.1010842 82.7912616,26.7805123 L59.5038326,50.0678739 L82.7912616,73.3552355 C85.4706897,76.0346636 85.4706897,80.3788748 82.7912616,83.0583029 C80.1118335,85.7377309 75.7676223,85.7377309 73.0881943,83.0583029 L49.8008326,59.7708739 L26.513471,83.0583029 C23.834043,85.7377309 19.4898318,85.7377309 16.8104037,83.0583029 C14.1309756,80.3788748 14.1309756,76.0346636 16.8104037,73.3552355 L40.0978326,50.0678739 L16.8104037,26.7805123 C14.1309756,24.1010842 14.1309756,19.756873 16.8104037,17.077445 C19.4898318,14.3980169 23.834043,14.3980169 26.513471,17.077445 Z'
            fill={fillColor}></path>
        )
      case DECREMENT:
        return (
          <path d='M85,49.5 C85,53.2117839 81.9910046,56.2207792 78.2792208,56.2207792 L22.7207792,56.2207792 C19.0089954,56.2207792 16,53.2117839 16,49.5 C16,45.7882161 19.0089954,42.7792208 22.7207792,42.7792208 L78.2792208,42.7792208 C81.9910046,42.7792208 85,45.7882161 85,49.5 Z'></path>
        )
      case DOLLAR:
        return (
          <g transform='translate(25.000000, 7.000000)'>
            <path d='M32.7985141,63.15625 C30.1714377,64.1605409 27.3642703,64.617934 24.5514889,64.5 L24.212941,64.5 C21.1628792,64.5898003 18.1354503,63.9539661 15.3836135,62.645625 C14.0233669,61.9159296 12.8581226,60.8748351 11.9845933,59.60875 L8.89703705,55.1878125 L0,61.3153125 L3.08755624,65.73625 C4.97176943,68.4461355 7.48427862,70.6673775 10.4137313,72.213125 L10.5626924,72.29375 C13.503119,73.7017591 16.6541061,74.627514 19.8930707,75.035 L19.8930707,86 L30.7266013,86 L30.7266013,74.7796875 C32.7842097,74.4287632 34.7993166,73.8658213 36.7392108,73.1 C40.3919065,71.7173771 43.5622961,69.3135765 45.8664604,66.1796875 C49.6034274,60.6673315 50.028685,53.5760289 46.9768973,47.6628125 L46.9091877,47.5284375 C45.5571989,45.2902082 43.7036644,43.3911897 41.4924224,41.97875 C39.3395541,40.600971 37.0005795,39.5332481 34.5454209,38.8075 C33.1912295,38.3909375 31.552658,37.9609375 29.683874,37.46375 C28.1807216,37.0471875 26.5421501,36.6575 24.0098123,36.12 C21.3854356,35.5701159 18.8089635,34.8152654 16.3044636,33.8625 C15.0713755,33.3707456 13.9786671,32.5863706 13.122114,31.578125 C12.4245583,30.676938 12.082653,29.5540054 12.1606382,28.4203125 C12.1606382,28.245625 12.1606382,28.084375 12.1606382,27.9096875 C12.2165525,26.9618583 12.5507805,26.0509859 13.122114,25.289375 C14.1388375,24.0624538 15.4730399,23.1333905 16.9815593,22.601875 C19.4661879,21.7743675 22.0815029,21.4010303 24.7004499,21.5 L24.8764948,21.5 C26.8485959,21.4964991 28.8120472,21.7586849 30.7130594,22.279375 C32.0130709,22.6445037 33.2373384,23.2359929 34.3287503,24.02625 C35.0219493,24.5206907 35.6146656,25.1407518 36.0756571,25.85375 L39.0142523,30.36875 L48.1279599,24.5503125 L45.1893648,20.0353125 C44.0005446,18.1903529 42.4704459,16.5854222 40.6799076,15.3053125 C38.5691343,13.7813331 36.2035468,12.6395031 33.6922803,11.9325 C32.7158959,11.6585749 31.7260463,11.4343243 30.7266013,11.260625 L30.7266013,0 L19.8930707,0 L19.8930707,11.0321875 C17.7236153,11.304099 15.5869383,11.79026 13.5148295,12.4834375 C10.1319748,13.6383977 7.13076536,15.6872721 4.83446306,18.409375 L4.68550201,18.5840625 C2.59953107,21.2119609 1.41346429,24.4320188 1.30002368,27.7753125 C1.07536173,31.458092 2.18484279,35.0992516 4.42820566,38.0415625 L4.60425053,38.27 C6.67916158,40.7864146 9.37591297,42.7262529 12.4314764,43.9003125 C15.4611939,45.0373924 18.5764323,45.9360025 21.7483128,46.5878125 C24.06398,47.0984375 25.5400485,47.4478125 26.867156,47.8240625 L27.0161171,47.8240625 C28.7359401,48.2540625 30.1849248,48.64375 31.4443227,49.02 C32.9124855,49.4496634 34.3121071,50.0830061 35.6016901,50.90125 C36.3713556,51.4089013 37.0186917,52.0787752 37.497558,52.863125 C37.9865838,53.8283069 38.2196175,54.9012464 38.1746537,55.980625 C38.1746537,56.06125 38.1746537,56.141875 38.1746537,56.2225 C38.2209782,57.5271754 37.8461642,58.8124317 37.1048425,59.8909375 C36.0283878,61.3799103 34.5291285,62.5167373 32.7985141,63.15625 L32.7985141,63.15625 Z'></path>
          </g>
        )
      case GRID:
        const gridIconSize = 15

        return (
          <g>
            <rect
              fill={strokeColor}
              x={43}
              y={43}
              rx={rounded}
              width={gridIconSize}
              height={gridIconSize}
            />
            <rect
              fill={strokeColor}
              x={gridIconSize}
              y={43}
              rx={rounded}
              width={gridIconSize}
              height={gridIconSize}
            />
            <rect
              fill={strokeColor}
              x={70}
              y={43}
              rx={rounded}
              width={gridIconSize}
              height={gridIconSize}
            />
            <rect
              fill={strokeColor}
              x={43}
              y={67}
              width={gridIconSize}
              height={gridIconSize}
              rx={rounded}
            />
            <rect
              fill={strokeColor}
              x={70}
              y={67}
              width={gridIconSize}
              height={gridIconSize}
              rx={rounded}
            />
            <rect
              fill={strokeColor}
              x={gridIconSize}
              y={67}
              width={gridIconSize}
              height={gridIconSize}
              rx={rounded}
            />
            <rect
              fill={strokeColor}
              x={43}
              y={18}
              width={gridIconSize}
              height={gridIconSize}
              rx={rounded}
            />
            <rect
              fill={strokeColor}
              x={gridIconSize}
              y={18}
              width={gridIconSize}
              height={gridIconSize}
              rx={rounded}
            />
            <rect
              fill={strokeColor}
              x={70}
              y={18}
              width={gridIconSize}
              height={gridIconSize}
              rx={rounded}
            />
          </g>
        )
      case INCREMENT:
        return (
          <path d='M50.5,15 C54.2117839,15 57.2207792,18.0089954 57.2207792,21.7207792 L57.22,42.779 L78.2792208,42.7792208 C81.9910046,42.7792208 85,45.7882161 85,49.5 C85,53.2117839 81.9910046,56.2207792 78.2792208,56.2207792 L57.22,56.22 L57.2207792,77.2792208 C57.2207792,80.9910046 54.2117839,84 50.5,84 C46.7882161,84 43.7792208,80.9910046 43.7792208,77.2792208 L43.779,56.22 L22.7207792,56.2207792 C19.0089954,56.2207792 16,53.2117839 16,49.5 C16,45.7882161 19.0089954,42.7792208 22.7207792,42.7792208 L43.779,42.779 L43.7792208,21.7207792 C43.7792208,18.0089954 46.7882161,15 50.5,15 Z'></path>
        )
      case LIST:
        return (
          <g>
            <rect
              fill={strokeColor}
              x={15}
              y={19}
              rx={rounded}
              width={70}
              height={12}
            />
            <rect
              fill={strokeColor}
              x={15}
              y={69}
              rx={rounded}
              width={70}
              height={12}
            />
            <rect
              fill={strokeColor}
              x={15}
              y={44}
              rx={rounded}
              width={70}
              height={12}
            />
          </g>
        )
      case MENU:
        return (
          <g transform='translate(10, 10)'>
            <rect
              fill={fillColor}
              x='0'
              y='0'
              width='80'
              height='80'
              rx='30'></rect>
          </g>
        )
      case NUMBER:
        return (
          <g>
            <rect fill={fillColor} x="10" y="30" width="80" height="10" rx="5"></rect>
            <path fill={fillColor} d="M-0.024814049,41.4975186 L70.024814,48.5024814 C72.8491722,48.7849172 75,51.1615552 75,54 C75,56.4990541 72.9741163,58.5249378 70.4750622,58.5249378 C70.3246996,58.5249378 70.1744304,58.517443 70.024814,58.5024814 L-0.024814049,51.4975186 C-2.84917219,51.2150828 -5,48.8384448 -5,46 C-5,43.5009459 -2.97411634,41.4750622 -0.475062189,41.4750622 C-0.324699585,41.4750622 -0.174430432,41.482557 -0.024814049,41.4975186 Z" transform="translate(35.000000, 50.000000) rotate(90.000000) translate(-35.000000, -50.000000) "></path>
            <path fill={fillColor} d="M29.975186,41.4975186 L100.024814,48.5024814 C102.849172,48.7849172 105,51.1615552 105,54 C105,56.4990541 102.974116,58.5249378 100.475062,58.5249378 C100.3247,58.5249378 100.17443,58.517443 100.024814,58.5024814 L29.975186,51.4975186 C27.1508278,51.2150828 25,48.8384448 25,46 C25,43.5009459 27.0258837,41.4750622 29.5249378,41.4750622 C29.6753004,41.4750622 29.8255696,41.482557 29.975186,41.4975186 Z" id="Rectangle" transform="translate(65.000000, 50.000000) rotate(90.000000) translate(-65.000000, -50.000000) "></path>
            <rect fill={fillColor} x="10" y="60" width="80" height="10" rx="5"></rect>
          </g>
        )
      case SEARCH:
        return (
          <path fill={fillColor} d="M45,14 C62.1208272,14 76,27.8791728 76,45 C76,51.3913432 74.0658134,57.3309328 70.7508445,62.2653648 L84.7279221,76.2426407 C87.0710678,78.5857864 87.0710678,82.3847763 84.7279221,84.7279221 C82.3847763,87.0710678 78.5857864,87.0710678 76.2426407,84.7279221 L62.2653648,70.7508445 C57.3309328,74.0658134 51.3913432,76 45,76 C27.8791728,76 14,62.1208272 14,45 C14,27.8791728 27.8791728,14 45,14 Z M45,26 C34.5065898,26 26,34.5065898 26,45 C26,55.4934102 34.5065898,64 45,64 C55.4934102,64 64,55.4934102 64,45 C64,34.5065898 55.4934102,26 45,26 Z"></path>
        )
      case UPDATE:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" x={15} y={15} width="70" height="70" viewBox="0 0 30 30">
            <path fill={fillColor} d="M 22.828125 3 C 22.316375 3 21.804562 3.1954375 21.414062 3.5859375 L 19 6 L 24 11 L 26.414062 8.5859375 C 27.195062 7.8049375 27.195062 6.5388125 26.414062 5.7578125 L 24.242188 3.5859375 C 23.851688 3.1954375 23.339875 3 22.828125 3 z M 17 8 L 5.2597656 19.740234 C 5.2597656 19.740234 6.1775313 19.658 6.5195312 20 C 6.8615312 20.342 6.58 22.58 7 23 C 7.42 23.42 9.6438906 23.124359 9.9628906 23.443359 C 10.281891 23.762359 10.259766 24.740234 10.259766 24.740234 L 22 13 L 17 8 z M 4 23 L 3.0566406 25.671875 A 1 1 0 0 0 3 26 A 1 1 0 0 0 4 27 A 1 1 0 0 0 4.328125 26.943359 A 1 1 0 0 0 4.3378906 26.939453 L 4.3632812 26.931641 A 1 1 0 0 0 4.3691406 26.927734 L 7 26 L 5.5 24.5 L 4 23 z"></path>
          </svg>
        )
      case PERCENTAGE:
        return (
          <g transform="matrix(2.15467,0,0,2.18012,-44.1592,-0.573332)">
            <path fill={fillColor} d="M24.318,13.435C24.318,10.88 24.961,8.707 26.247,6.917C27.533,5.127 29.396,4.231 31.838,4.231C34.084,4.231 35.944,5.033 37.416,6.636C38.889,8.239 39.626,10.595 39.626,13.704C39.626,16.731 38.881,19.063 37.392,20.699C35.903,22.334 34.068,23.152 31.887,23.152C29.722,23.152 27.919,22.347 26.479,20.735C25.039,19.124 24.318,16.691 24.318,13.435ZM31.96,7.259C30.869,7.259 29.962,7.731 29.238,8.675C28.513,9.619 28.151,11.352 28.151,13.875C28.151,16.17 28.518,17.785 29.25,18.721C29.982,19.657 30.886,20.125 31.96,20.125C33.067,20.125 33.982,19.653 34.707,18.709C35.431,17.765 35.793,16.04 35.793,13.533C35.793,11.222 35.427,9.598 34.694,8.663C33.962,7.727 33.05,7.259 31.96,7.259ZM31.984,41.951L51.564,4.231L55.129,4.231L35.622,41.951L31.984,41.951ZM47.463,32.234C47.463,29.663 48.106,27.486 49.392,25.704C50.677,23.921 52.549,23.03 55.007,23.03C57.253,23.03 59.112,23.832 60.585,25.435C62.058,27.038 62.795,29.394 62.795,32.503C62.795,35.53 62.05,37.862 60.561,39.497C59.072,41.133 57.229,41.951 55.031,41.951C52.867,41.951 51.064,41.141 49.624,39.522C48.183,37.902 47.463,35.473 47.463,32.234ZM55.129,26.058C54.022,26.058 53.107,26.53 52.382,27.474C51.658,28.418 51.296,30.151 51.296,32.674C51.296,34.952 51.662,36.564 52.395,37.508C53.127,38.452 54.03,38.924 55.104,38.924C56.228,38.924 57.151,38.452 57.875,37.508C58.6,36.564 58.962,34.838 58.962,32.332C58.962,30.021 58.596,28.397 57.863,27.461C57.131,26.525 56.219,26.058 55.129,26.058Z" style={{fillRule: "nonzero"}}/>
          </g>
        )
      default:
        return (
          <rect
            fill='hsla(0,0%,0%,0.1)'
            x={15}
            y={15}
            width={70}
            height={70}
            rx={20}
          />
        )
    }
  }
}

export default Icon
